import { useState } from 'react';
import { MessageSquare, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { ReviewSessionSummary } from '@/types/api';

interface RevisionDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly session: ReviewSessionSummary;
  readonly onConfirm: (notes: string) => void;
  readonly isLoading: boolean;
}

const MIN_LENGTH = 20;

export function RevisionDialog({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: RevisionDialogProps) {
  const [notes, setNotes] = useState('');
  const isValid = notes.trim().length >= MIN_LENGTH;

  const handleSubmit = () => {
    if (isValid) {
      onConfirm(notes.trim());
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-compliance-medium" />
            Request Revision
          </DialogTitle>
          <DialogDescription>
            Describe what needs to be changed. The AI will regenerate the draft
            based on your feedback.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="revision-notes">Revision instructions</Label>
          <Textarea
            id="revision-notes"
            placeholder="e.g. The proposed change to section 3.2 conflicts with our existing policy on patient consent. Please revise to align with..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={5}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Minimum {MIN_LENGTH} characters
            </p>
            <p className="tabular-nums text-xs text-muted-foreground">
              {notes.trim().length}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            className="bg-teal text-white hover:bg-teal/90"
            onClick={handleSubmit}
            disabled={!isValid || isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
